import React, { useContext } from 'react';

import styled from '@emotion/styled';

import { CopyKeyButton } from '../../../Button/CopyKeyButton';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import GearIcon from '../../../Icons/GearIcon';
import LargeCheckMarkIcon from '../../../Icons/LargeCheckMarkIcon';
import ModalContainer from '../../ModalContainer';
import { ModalContext } from '../../ModalContext';

interface GenerateApiKeyModalProps {
  loading: boolean;
  newlyGeneratedApiKey?: string;
}

const GenerateApiKeyModal: React.FC<GenerateApiKeyModalProps> = ({
  loading,
  newlyGeneratedApiKey,
}) => {
  const modalContext = useContext(ModalContext);
  const onClickCancel = (e: any) => {};

  const closeModal = () => {
    modalContext?.setShowModal(false);
  };

  return (
    <ModalContainer removeTitleAndControl>
      {loading ? (
        <ViewWrapper>
          <View>
            <IconArea>
              <GearIcon />
            </IconArea>
            <BoldText>Generating New Keys</BoldText>
            <Description>
              Securely creating new encrypted access keys
            </Description>
          </View>
          <Button onClick={onClickCancel}>Generating...</Button>
        </ViewWrapper>
      ) : (
        <ViewWrapper>
          <View>
            <IconArea>
              <LargeCheckMarkIcon />
            </IconArea>
            <BoldText>API key generated.</BoldText>
            <Description>
              Please copy and keep your secret key in a safe location.
            </Description>
          </View>
          <ButtonArea>
            <CopyKeyButton
              apiKey={newlyGeneratedApiKey ?? ''}
              label='Copy secret key'
            />
            <FormSubmitButton
              onClick={closeModal}
              btnType='button'
              className='fill'
            >
              Done
            </FormSubmitButton>
          </ButtonArea>
        </ViewWrapper>
      )}
    </ModalContainer>
  );
};

export default GenerateApiKeyModal;

const ViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const View = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 400px;
  padding: 40px 18px 24px;
  background-color: ${({ theme }) => theme.palette.pageBgColor};

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const BoldText = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 20px;
  color: ${({ theme }) => theme.palette.logirateBlack};
`;

const Button = styled.button`
  border: none;
  background-color: ${({ theme }) => theme.palette.blueGrayBg};
  color: ${({ theme }) => theme.palette.mediumGrayText};
  padding: 14px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  border-radius: 4px;
  cursor: pointer;
`;

const IconArea = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 16px;
  background: ${({ theme }) => theme.palette.logiratePrimaryLight};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;

  & > svg {
    color: ${({ theme }) => theme.palette.logiratePrimary};
  }
`;

const Description = styled.div`
  font-size: 14px;
  line-height: 25px;
  font-weight: 400;
  text-align: center;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const ButtonArea = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;
